import { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSessionData } from 'utils.session';
import logo from '../assets/images/GOVT_LOGO.png';
import { MultiLoginModal } from '../mulipleLogin/MultiLoginModal';
import { useMultiUserLoginMutation } from '../mulipleLogin/multiLoginApi.service';
import { toast } from 'react-toastify';
import { useUI } from 'shared/UI/Uicontext';
const DefaultAvatar = ({ size = 36 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 36 36"
    className="default-avatar-svg"
  >
    <defs>
      <clipPath id={`avatarClip-${size}`}>
        <circle cx="18" cy="18" r="18" />
      </clipPath>
    </defs>
    <circle cx="18" cy="18" r="18" fill="#556080" />
    <g clipPath={`url(#avatarClip-${size})`}>
      <circle cx="18" cy="14.5" r="6" fill="#e7ecec" />
      <ellipse cx="18" cy="32" rx="11" ry="10.5" fill="#e7ecec" />
    </g>
  </svg>
);

const Header = () => {
  const { showLoader, hideLoader } = useUI();
  const angularAppUrl =
    process.env.REACT_APP_API_environment === 'LOCAL'
      ? 'http://localhost:4200'
      : process.env.REACT_APP_API_environment === 'STAGING'
        ? 'https://tnemis-staging.tnsed.com'
        : 'https://tnemis.tnschools.gov.in';
  const reactAppUrl =
    process.env.REACT_APP_API_environment === 'LOCAL'
      ? 'http://localhost:3000'
      : process.env.REACT_APP_API_environment === 'STAGING'
        ? 'https://tnemis-react-staging.tnsed.com'
        : 'https://tnemis-react-prod.tnschools.gov.in';
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const iframeReadyRef = useRef<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [session, setSession] = useState<any>(null);
  const [showMultiLogin, setShowMultiLogin] = useState(false);
  const [districtName, setDistrictName] = useState('');
  const [blockName, setBlockName] = useState('');
  const [masterData, setMasterData] = useState<any[]>([]);
  const [masterDataLoaded, setMasterDataLoaded] = useState(false);

  const [multiUserLogin] = useMultiUserLoginMutation();

  // Load master JSON data on mount
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const response = await fetch(
          'https://d1wpyxz35bzzz4.cloudfront.net/st_dist_blk_sch_master.json'
        );
        const data = await response.json();
        setMasterData(data);
        setMasterDataLoaded(true);
      } catch (error) {
        console.error('Failed to fetch master data:', error);
      }
    };
    loadMasterData();
  }, []);

  // Re-read session on every route change
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      queueMicrotask(() => setSession(null));
      return;
    }
    try {
      const data = getSessionData();
      queueMicrotask(() => setSession(data));

      // Extract district and block IDs from session
      if (data && masterDataLoaded) {
        const districtId =
          data.districtId || data.districtId || data.districtId || '';
        const blockId = data.blockId || data.blockId || data.blockId || '';
        const districtNameFromSession =
          data.districtName || data.districtName || '';
        const blockNameFromSession = data.blockName || data.blockName || '';

        // First check if district name is directly available in session
        queueMicrotask(() => {
          if (districtNameFromSession) {
            setDistrictName(districtNameFromSession);
          } else if (districtId && masterData.length > 0) {
            const district = masterData.find(
              (x: any) => x.district_id == districtId
            );
            if (district) {
              setDistrictName(district.district_name);
            }
          }

          // Same for block
          if (blockNameFromSession) {
            setBlockName(blockNameFromSession);
          } else if (blockId && masterData.length > 0) {
            const block = masterData.find((x: any) => x.block_id == blockId);
            if (block) {
              setBlockName(block.block_name);
            }
          }
        });
      }
    } catch {
      queueMicrotask(() => setSession(null));
    }
  }, [location.pathname, masterData, masterDataLoaded]);

  // Determine if we should show district/block name (usertype condition)
  const shouldShowLocation = useMemo(() => {
    const usertype = session?.usertypeid || session?.userTypeId;
    // Exclude specific user types (mirrors Angular condition)
    const excludedTypes = [5, 15, 13, 44, 51];
    return usertype && !excludedTypes.includes(usertype);
  }, [session]);

  // Get location display name (district or block) - for header
  const locationDisplayName = useMemo(() => {
    if (districtName) return districtName;
    if (blockName) return blockName;
    return '';
  }, [districtName, blockName]);

  const displayName = useMemo(() => {
    return (
      session?.teacher_name || session?.name || session?.username || 'User'
    );
  }, [session]);

  // Hidden iframe bridge for Angular cross-domain localStorage sync
  useEffect(() => {
    const iframe = document.createElement('iframe');
    iframe.src = `${angularAppUrl}/receiver.html`;
    iframe.style.display = 'none';
    iframe.onload = () => {
      iframeRef.current = iframe;
      iframeReadyRef.current = true;
    };
    document.body.appendChild(iframe);
    return () => {
      iframeReadyRef.current = false;
      iframeRef.current = null;
      document.body.removeChild(iframe);
    };
  }, []);

  // Close dropdown when clicking outside the user block
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.user-account-block')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // const handleLogout = () => {
  //   setDropdownOpen(false);
  //   // setSession(null);

  //   const targetOrigin = new URL(angularAppUrl).origin;
  //   localStorage.clear();
  //   const iframe = iframeRef.current;
  //   if (!iframe || !iframe.contentWindow || !iframeReadyRef.current) {
  //     window.location.href = angularAppUrl;
  //     return;
  //   }

  //   const logoutListener = (event: MessageEvent) => {
  //     if (event.origin === targetOrigin && event.data === 'clear_success') {
  //       window.removeEventListener('message', logoutListener);
  //       window.location.href = angularAppUrl;
  //     }
  //   };

  //   window.addEventListener('message', logoutListener);
  //   iframe.contentWindow.postMessage('COMMAND_CLEAR_STORAGE', targetOrigin);

  //   setTimeout(() => {
  //     window.removeEventListener('message', logoutListener);
  //     window.location.href = reactAppUrl;
  //   }, 1000);
  // };

  const handleLogout = () => {
    showLoader();
    setDropdownOpen(false);

    const targetOrigin = new URL(angularAppUrl).origin;
    localStorage.clear();

    const iframe = iframeRef.current;

    // Fire the cross-domain clear command in the background — don't wait on it
    if (iframe && iframe.contentWindow && iframeReadyRef.current) {
      iframe.contentWindow.postMessage('COMMAND_CLEAR_STORAGE', targetOrigin);
    }

    // Redirect to Angular login (Angular is the primary auth app)
    setTimeout(() => {
      hideLoader();
      window.location.href = `${reactAppUrl}/auth/login`;
    }, 300);
  };
  const handleResetPassword = () => {
    setDropdownOpen(false);
    navigate('/reset-password');
  };

  const handleSwitchUser = () => {
    setDropdownOpen(false);
    navigate('/switch-user');
  };

  const handleMultiLogin = () => {
    setDropdownOpen(false);
    setShowMultiLogin(true);
  };

  const handleBackToUser = async () => {
    setDropdownOpen(false);
    const backUserName = localStorage.getItem('backUser');
    if (!backUserName) return;

    try {
      const res = await multiUserLogin({ username: backUserName }).unwrap();

      if (!res?.dataStatus) {
        toast.error(res?.message || 'Unable to switch back');
        return;
      }

      // Token may be at different paths depending on API version
      const restoredToken =
        res?.records?.token || res?.result?.token || res?.token;
      if (!restoredToken) {
        toast.error('Token not found');
        console.error('[Back To User] API response:', res);
        return;
      }

      localStorage.setItem('token', restoredToken);
      localStorage.setItem('user_token', restoredToken);
      localStorage.removeItem('backUser');

      window.location.reload();
    } catch (err) {
      console.error('[Back To User] error:', err);
      toast.error('Unable to switch back');
    }
  };

  const backUser = localStorage.getItem('backUser');

  // Get dropdown user ID (shows teacher ID for teachers, username for others)
  const getDropdownUserId = () => {
    // For teachers, show teacher ID
    if (session?.usertypeid === 14) {
      return (
        session?.teacherId || session?.teacher_id || session?.username || '—'
      );
    }
    // For others, show username or teacherId
    return session?.teacherId || session?.username || '—';
  };

  // Get dropdown district name - returns empty string if not available
  const getDropdownDistrict = () => {
    return locationDisplayName || '';
  };

  // Get dropdown designation
  const getDropdownDesignation = () => {
    return session?.type_teacher || session?.designation || '—';
  };

  return (
    <>
      <header className="app-header d-flex align-items-center justify-content-between px-3">
        <div className="logo-area d-flex align-items-center">
          <img src={logo} alt="Logo" height={40} />
          <h2 className="app-title mb-0 fs-3 ms-2 fw-semibold">
            TN EMIS
            {shouldShowLocation && locationDisplayName && (
              <span className="location-name">
                &nbsp;-&nbsp;{locationDisplayName}
              </span>
            )}
          </h2>
        </div>

        <div className="header-right d-flex align-items-center gap-3">
          <div className="bell-wrap">
            <i className="pi pi-bell"></i>
            <span className="bell-badge">0</span>
          </div>

          <div className="user-account-block">
            <div
              className="user-trigger"
              onClick={() => setDropdownOpen((p) => !p)}
            >
              <DefaultAvatar />
              <div className="user-block">
                <span className="user">
                  {displayName}
                  <i
                    className={`pi pi-chevron-down user-chevron ${dropdownOpen ? 'open' : ''}`}
                  ></i>
                </span>
                <span className="user-status">Active</span>
              </div>
            </div>

            {dropdownOpen && (
              <div className="user-dropdown-panel">
                {/* User info section */}
                <div className="dropdown-avatar-wrap">
                  <DefaultAvatar size={64} />
                </div>

                {/* User ID - shows teacher ID for teachers, username for others */}
                <div className="dropdown-userid">{getDropdownUserId()}</div>

                {/* District/Block Name - only show if district exists */}
                {getDropdownDistrict() && (
                  <div className="dropdown-district">
                    {getDropdownDistrict()}
                  </div>
                )}

                {/* Designation */}
                <div className="dropdown-designation">
                  {getDropdownDesignation()}
                </div>

                <div className="dropdown-divider" />

                {/* Reset Password */}
                <button className="dropdown-item" onClick={handleResetPassword}>
                  <i className="pi pi-key"></i>
                  Reset Password
                </button>

                {/* Switch User */}
                <button className="dropdown-item" onClick={handleSwitchUser}>
                  <i className="pi pi-users"></i>
                  Switch User
                </button>

                {/* Multiple Login — only for PM users (switch_designation non-empty) and only when NOT already switched */}
                {!backUser && session?.switch_designation && (
                  <button className="dropdown-item" onClick={handleMultiLogin}>
                    <i className="pi pi-user-plus"></i>
                    Multiple Login
                  </button>
                )}

                {/* Back To User — only when currently in a switched session */}
                {backUser && (
                  <button className="dropdown-item" onClick={handleBackToUser}>
                    <i className="pi pi-user-plus"></i>
                    Back To User
                  </button>
                )}

                {/* Log Out */}
                <button className="dropdown-item" onClick={handleLogout}>
                  <i className="pi pi-sign-out"></i>
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Multiple Login modal */}
      <MultiLoginModal
        visible={showMultiLogin}
        onHide={() => setShowMultiLogin(false)}
        userName={session?.username ?? ''}
        switchDesignation={session?.switch_designation ?? ''}
      />
    </>
  );
};

export default Header;
